import { Link } from 'react-router-dom';
import { locations } from '../../../mocks/locations';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47488.96!2d-87.87!3d41.95!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fcf594a21d73d%3A0x29c65c88f0ec9b5d!2s5801%20N%20Northwest%20Hwy%2C%20Chicago%2C%20IL%2060631!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus';

export default function ServiceAreas() {
  const featured = locations.filter((l) => l.featured);
  const others = locations.filter((l) => !l.featured);

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-cream rounded-full translate-x-1/3 translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <RevealOnScroll className="text-center mb-14">
          <span className="text-brand-green text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
            <span className="w-8 h-px bg-brand-green inline-block" />
            Where We Work
            <span className="w-8 h-px bg-brand-green inline-block" />
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-brand-dark mt-4 mb-5">
            Serving Chicago &amp;<br />
            <span className="text-brand-green italic">All of Chicagoland</span>
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto leading-relaxed">
            From Chicago&apos;s North Side neighborhoods to the western suburbs — we bring 35+ years of
            local expertise to every roofing and construction project.
          </p>
        </RevealOnScroll>

        {/* Map — large format */}
        <RevealOnScroll className="mb-12">
          <div className="rounded-2xl overflow-hidden border border-brand-cream-3 h-80 lg:h-[480px] relative">
            <iframe
              src={MAP_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Green World Construction - Chicago Chicagoland Service Area"
            />
            {/* Map overlay badge */}
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-brand-green/10 rounded-lg">
                <i className="ri-map-pin-2-fill text-brand-green text-base" />
              </div>
              <div>
                <div className="font-bold text-brand-dark text-xs">5801 N Northwest Hwy</div>
                <div className="text-gray-500 text-xs">Chicago, Illinois 60631</div>
              </div>
            </div>
          </div>
        </RevealOnScroll>

        {/* Featured cities */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px bg-brand-cream-3 flex-1" />
            <h3 className="text-sm font-bold text-brand-dark uppercase tracking-widest flex items-center gap-2">
              <i className="ri-map-pin-2-fill text-brand-gold text-sm" />
              Primary Service Cities
            </h3>
            <div className="h-px bg-brand-cream-3 flex-1" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            {featured.map((loc, idx) => (
              <RevealOnScroll key={loc.id} delay={idx * 60}>
                <Link
                  to={`/locations/${loc.slug}`}
                  className="group flex items-center gap-2 bg-brand-green text-white hover:bg-brand-green-dark transition-all px-4 py-3 rounded-xl cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-fill text-brand-gold text-xs" />
                  </div>
                  <span className="text-xs font-semibold">{loc.city}, IL</span>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>

        {/* All other locations */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px bg-brand-cream-3 flex-1" />
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">All Service Areas</span>
            <div className="h-px bg-brand-cream-3 flex-1" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {others.map((loc, idx) => (
              <RevealOnScroll key={loc.id} delay={idx * 30}>
                <Link
                  to={`/locations/${loc.slug}`}
                  className="group bg-brand-cream hover:bg-brand-cream-3 border border-brand-cream-3 hover:border-brand-gold/40 rounded-lg px-3 py-2.5 flex items-center gap-1.5 cursor-pointer transition-all"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-brand-green text-xs" />
                  </div>
                  <span className="text-xs text-gray-600 group-hover:text-brand-green transition-colors font-medium">{loc.city}, IL</span>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>

        {/* CTA */}
        <RevealOnScroll className="text-center">
          <Link
            to="/locations"
            className="inline-flex items-center gap-2 border-2 border-brand-green text-brand-green hover:bg-brand-green hover:text-white text-sm font-bold px-10 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-map-2-line" />
            Check Service Availability Near You
          </Link>
        </RevealOnScroll>
      </div>
    </section>
  );
}
